import { executeGet } from '@/services/lukita'

export const FIRST_PAGE_LIMIT = 200
export const NEXT_PAGE_LIMIT = 1000

interface PageStatement {
  id: number
  file_name: string
  file_url: string
}

interface PagedRow {
  statement_id: number
}

interface Page<Row> {
  rows: Row[]
  hasMore: boolean
}

// progressive shows each page as it lands; whole swaps the list in once every page is in.
export type LoadMode = 'progressive' | 'whole'

// Paged rows leave out their statement's file name and link; the page carries its statements instead.
const withStatementFiles = <Row extends PagedRow>(
  rows: Row[],
  statements: PageStatement[],
): Row[] => {
  const statementsById = new Map(
    statements.map((statement) => [statement.id, statement]),
  )
  return rows.map((row) => {
    const statement = statementsById.get(row.statement_id)
    if (!statement) return row
    return {
      ...row,
      statement_file_name: statement.file_name,
      statement_file_url: statement.file_url,
    }
  })
}

interface LoadPagesOptions<Row> {
  path: string
  root: string
  mode: LoadMode
  isStale: () => boolean
  store: (rows: Row[], arrival: { isFirst: boolean }) => void
  onProgress: (progress: { hasMore: boolean }) => void
}

const fetchPage = async <Row extends PagedRow>(
  path: string,
  root: string,
  limit: number,
  after: string | null,
): Promise<Page<Row> & { next: string | null }> => {
  const response = await executeGet(path, {
    queryParams: after ? { limit, after } : { limit },
  })
  const body = await response.json()

  // An API without paging ignores limit and sends every row at once.
  if (Array.isArray(body)) return { rows: body, hasMore: false, next: null }

  const next = body.next_cursor ?? null
  return {
    rows: withStatementFiles(body[root] as Row[], body.statements ?? []),
    hasMore: next !== null,
    next,
  }
}

// The first page is small so the ledger paints fast; the rest arrive in bigger pages behind it.
async function* fetchPages<Row extends PagedRow>(
  path: string,
  root: string,
): AsyncGenerator<Page<Row>> {
  let after: string | null = null
  let limit = FIRST_PAGE_LIMIT

  do {
    const page: Page<Row> & { next: string | null } = await fetchPage<Row>(
      path,
      root,
      limit,
      after,
    )
    yield page
    after = page.next
    limit = NEXT_PAGE_LIMIT
  } while (after)
}

export const loadPages = async <Row extends PagedRow>({
  path,
  root,
  mode,
  isStale,
  store,
  onProgress,
}: LoadPagesOptions<Row>): Promise<void> => {
  const buffered: Row[] = []
  let isFirst = true

  for await (const { rows, hasMore } of fetchPages<Row>(path, root)) {
    if (isStale()) return

    if (mode === 'whole') {
      buffered.push(...rows)
      if (!hasMore) store(buffered, { isFirst: true })
      continue
    }

    store(rows, { isFirst })
    onProgress({ hasMore })
    isFirst = false
  }
}
